// @flow
import { call, put, takeEvery, select, spawn } from 'redux-saga/effects';
import type { Saga } from 'redux-saga';
import {
  CART,
  receiveUpdateCart,
  receiveUpdateCartError,
} from 'pages/MyAccount/services/actions/cartActions';
import {
  toggleSoldOut,
  hideSponsoredWarning,
} from 'pages/MyAccount/services/actions/productsActions';
import type {
  RequestAddToCartAction,
  RequestRemoveFromCartAction,
} from 'pages/MyAccount/services/actions/cartActions';
import { updateCart as _updateCart } from 'pages/MyAccount/services/api/carts';
import { getCampaignType } from 'pages/MyAccount/utils/campaign';
import { segmentEnum, track, btnLocationEnum } from 'pages/MyAccount//utils/segment';
import { selectTrackingData } from 'pages/MyAccount/selectors/trackingSelector';
import type { UpdateCartResponse } from 'pages/MyAccount/sharedTypes';

const { REQUEST_ADD_TO_CART, REQUEST_REMOVE_FROM_CART } = CART;

// WATCHER SAGAS
export function* watchAddToCart(): Saga<void> {
  yield takeEvery(REQUEST_ADD_TO_CART, updateCart);
}

export function* watchRemoveFromCart(): Saga<void> {
  yield takeEvery(REQUEST_REMOVE_FROM_CART, updateCart);
}

// WORKER SAGAS
// handle side effects when adding/removing products
function* updateCart(
  action: RequestAddToCartAction | RequestRemoveFromCartAction
): Saga<void> {
  const {
    variantId,
    productId,
    buttonLocation,
    ctaName,
    selectedCategoryId,
    type,
  } = action;
  try {
    const updateType = type === CART.REQUEST_ADD_TO_CART ? 'ADD' : 'REMOVE';

    yield put(hideSponsoredWarning());
    const queryParams = {
      operation: updateType,
      campaignType: getCampaignType(),
    };

    const cartResponse: {|
      success: boolean,
      value: {|
        cartDetails: UpdateCartResponse,
      |},
    |} = yield call(_updateCart, variantId, queryParams);

    if (cartResponse.success) {
      yield put(toggleSoldOut(variantId, false));
      yield put(receiveUpdateCart(variantId, cartResponse.value.cartDetails));
    } else {
      yield put(receiveUpdateCartError(variantId));
      yield put(toggleSoldOut(variantId, true));
    }

    yield spawn(
      handleUpdateCartTracking,
      updateType,
      productId,
      variantId,
      selectedCategoryId,
      buttonLocation,
      ctaName
    );
  } catch {
    yield put(receiveUpdateCartError(variantId));
  }
}

function* handleUpdateCartTracking(
  updateType: 'ADD' | 'REMOVE',
  productId: string,
  variantId: string,
  selectedCategoryId: string | null,
  buttonLocation: string,
  ctaName: string
): Saga<void> {
  // track
  const trackDisplayResults =
    buttonLocation === btnLocationEnum.productCard ||
    buttonLocation === btnLocationEnum.productAccordion;
  const trackingDataFromState = yield select((state) =>
    selectTrackingData(
      state,
      { productId, variantId, selectedCategoryId },
      trackDisplayResults
    )
  );

  const trackingData = {
    ...trackingDataFromState,
    cta_name: ctaName,
    experience: buttonLocation,
  };

  const FBTrackingData = {
    value:
      //not all products have a sale price, so we use retail price
      trackingDataFromState.sale_price || trackingDataFromState.retail_price,
    content_ids: [trackingDataFromState.product_id],
    content_type: 'product',
    currency: 'USD',
  };

  if (updateType === 'ADD') {
    track(segmentEnum.FBProductAdded, { ...trackingData, ...FBTrackingData });
    track(segmentEnum.productAdded, trackingData);
  } else {
    track(segmentEnum.productRemoved, trackingData);
  }
}
